import { useFilteredAvailableTimes } from "api/api-hooks/use-available-times";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useSectionNameMapping } from "components/selection-components/summary/use-section-name-mapping";
import { useBookingStateContext } from "hooks/use-booking-state";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Hook to get reserved activities with distinct start times and proper section names.
 * Returns formatted strings for display in activity badges.
 * @param excludeSectionId - Optional section ID to exclude from the results
 */
export const useBookedActivities = (excludeSectionId?: number) => {
    const { t } = useTranslation();
    const { data: booking } = useWebBooking();
    const { date } = useBookingStateContext();
    const availableTimesData = useFilteredAvailableTimes(date);

    const sectionNameMap = useSectionNameMapping();

    return useMemo(() => {
        if (!booking?.articles || !availableTimesData) return [];

        // Group by start time to avoid duplicates and use section names
        const uniqueStartTimes = new Map<
            string,
            {
                sectionName: string;
                start: moment.Moment;
                end: moment.Moment;
                ruleId?: number;
            }
        >();

        booking.articles.forEach((article) => {
            // Skip articles that belong to the excluded section
            if (excludeSectionId && sectionNameMap.get(article.ruleId)?.id === excludeSectionId) {
                return;
            }

            const startTimeKey = article.start.format("HH:mm");
            if (!uniqueStartTimes.has(startTimeKey)) {
                const sectionName =
                    sectionNameMap.get(article.ruleId)?.name || article.articleGroupName;

                uniqueStartTimes.set(startTimeKey, {
                    sectionName,
                    start: article.start,
                    end: article.end,
                    ruleId: article.ruleId,
                });
            }
        });

        return Array.from(uniqueStartTimes.values()).map((activity) => {
            const recoupTime =
                booking.webBookingRules?.find((rule) => rule.id === activity.ruleId)?.recoupTime ||
                0;

            return t("activityModal.reservedActivityBadge", {
                name: activity.sectionName,
                start: activity.start.format("HH:mm"),
                end: activity.end.clone().subtract(recoupTime, "minutes").format("HH:mm"),
            });
        });
    }, [booking, availableTimesData, t, sectionNameMap, excludeSectionId]);
};
